import * as Sentry from "@sentry/react";
import cx from "classnames";
import { Form, Formik } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Checkbox from "../../common/components/Forms/Checkbox/Checkbox";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { getDeclaration, recordAcceptance, setCurrentPageProgress } from "../api/ApiAppForm";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import AFQuestionPlaceholder from "../components/placeholders/AFQuestionPlaceholder";
import { PageType } from "../enums/PageType";
import { useAFForm } from "../hooks/useAFForm";
import { IAFDeclaration, IPageInfo } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import { IViewContext, ViewContext } from "../providers/ViewStateProvider";
import styles from "../scss/AFDeclaration.module.scss";
import { redirectTo404Page } from "../shared/AppFormHelper";

interface IAFDeclarationsProps {
    applicantToken: string;
}

const AFDeclarationsPage: React.FC = () => {
    const history = useHistory();
    const params = useParams<IAFDeclarationsProps>();
    const applicantToken = params.applicantToken;
    const { setError } = useContext<IViewContext>(ViewContext);

    const [isReady, setIsReady] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [declarationInfo, setDeclarationInfo] = useState<IAFDeclaration>();
    const [declarationAgree, setDeclarationAgree] = useState(false);
    const [policyAgree, setPolicyAgree] = useState(false);
    const bottomRef = useRef(null);

    const { selectors, actions } = useContext(AppFormControllerContext);

    const applicant = selectors.getApplicantFromToken(applicantToken);
    if (!applicant || !applicant?.applicationId) {
        redirectTo404Page();
        return;
    }

    const {
        getCurrentPage
    } = useAFForm({ pageType: PageType.Declarations, currentClientNumber: applicant?.clientNumber ?? 1 });



    const handleComplete = (): void => {
        setIsProcessing(true);
        if (isProcessing) return;
        const pageInfo: IPageInfo = {
            pageType: PageType.Payment,
            currentClientNumber: 1
        }
        if (declarationInfo?.applicantHasAcceptedDeclaration || declarationInfo?.applicantHasAcceptedPrivacyPolicy) {
            setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo)
            history.push(`/app-form/submit/${selectors.getApplicationId()}/payment`);
            return;
        }
        recordAcceptance(applicantToken)
            .then((res) => {
                setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo)
                history.push(`/app-form/submit/${selectors.getApplicationId()}/payment`);
            })
            .catch(() => {
                useErrorLogger();
                setError("There was a problem recording your confirmation.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicantToken: ${applicantToken} on the Declaration page. There was a problem recording the confirmation.`, "error");
                }
            })
            .finally(() => setIsProcessing(true));
    };

    const handleBack = (e): void => {
        e.preventDefault();
        history.push(`/app-form/details/${applicantToken}`)
    };

    const handleGetDeclaration = (): void => {
        setIsReady(false);
        getDeclaration(applicantToken)
            .then((res) => {
                setDeclarationInfo(res);
                setDeclarationAgree(res?.applicantHasAcceptedDeclaration || false);
                setPolicyAgree(res?.applicantHasAcceptedPrivacyPolicy || false);
            })
            .catch(() => {
                useErrorLogger();
                setError("There was a problem retrieving the declaration.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicantToken: ${applicantToken} on the Declaration page. There was a problem retrieving the declaration.`, "error");
                }
            })
            .finally(() => setIsReady(true));
    };

    const handleDeclarationButton = (isProcessing): boolean => {
        return (
            isProcessing ||
            (declarationInfo?.hasDeclarationHeader &&
                declarationInfo?.hasDeclaration &&
                !declarationAgree) ||
            (declarationInfo?.hasPrivacyPolicyHeader &&
                declarationInfo?.hasPrivacyPolicy &&
                !policyAgree) ||
            false
        );
    };


    useEffect(() => {
        if (document.getElementById("declaration")) {
            document.getElementById("declaration").tabIndex = 1;
        }
        if (isReady) return;
        handleGetDeclaration();
    }, [isReady]);


    return (
        <AFLayout currentPage={getCurrentPage()}>
            {!isReady && <AFQuestionPlaceholder />}
            {isReady && (
                <div className={cx(styles["layout"])}>
                    <Formik initialValues={{}} onSubmit={handleComplete}>
                        {() => (
                            <Form autoComplete="off"
                                className={cx(
                                    styles["af-declaration-container"],
                                    styles["layout-flex-column--centered"]
                                )}>
                                <label>Declaration</label>

                                <div className={styles["af-declaration-content"]}>
                                    {declarationInfo?.hasDeclarationHeader && (
                                        <h2>{declarationInfo?.declarationHeader}</h2>
                                    )}

                                    {declarationInfo?.hasDeclaration && (
                                        <div
                                            className={
                                                styles["af-declaration-description"]
                                            }
                                            dangerouslySetInnerHTML={{
                                                __html: declarationInfo?.declaration || ""
                                            }}></div>
                                    )}

                                    {declarationInfo?.hasDeclarationHeader &&
                                        declarationInfo?.hasDeclaration && (
                                            <Checkbox
                                                id={"declaration"}
                                                name={"declaration"}
                                                checked={declarationAgree}
                                                onChange={(e) => {
                                                    setDeclarationAgree(e.target.checked);
                                                    if (e.target.checked) {
                                                        bottomRef.current?.scrollIntoView(
                                                            { behavior: "smooth" }
                                                        );
                                                    }
                                                }}
                                                onBlur={() => { }}
                                                size="m"
                                                className={styles["af-declaration-check"]}
                                                label={
                                                    <span
                                                        className={cx(
                                                            "space-pre-m",
                                                            styles[
                                                            "af-declaration-check-label"
                                                            ]
                                                        )}>
                                                        I agree
                                                    </span>
                                                }
                                            />
                                        )}

                                    {declarationInfo?.hasPrivacyPolicyHeader && (
                                        <h2>{declarationInfo?.privacyPolicyHeader}</h2>
                                    )}

                                    {declarationInfo?.hasPrivacyPolicy && (
                                        <div
                                            className={
                                                styles["af-declaration-description"]
                                            }
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    declarationInfo?.privacyPolicy || ""
                                            }}></div>
                                    )}

                                    {declarationInfo?.hasPrivacyPolicyHeader &&
                                        declarationInfo?.hasPrivacyPolicy && (
                                            <Checkbox
                                                id={"policy"}
                                                name={"policy"}
                                                checked={policyAgree}
                                                onChange={(e) => {
                                                    setPolicyAgree(e.target.checked);
                                                    if (e.target.checked) {
                                                        bottomRef.current?.scrollIntoView(
                                                            { behavior: "smooth" }
                                                        );
                                                    }
                                                }}
                                                onBlur={() => { }}
                                                size="m"
                                                className={styles["af-declaration-check"]}
                                                label={
                                                    <span
                                                        className={cx(
                                                            "space-pre-m",
                                                            styles[
                                                            "af-declaration-check-label"
                                                            ]
                                                        )}>
                                                        I agree
                                                    </span>
                                                }
                                            />
                                        )}
                                    <AFNavigation
                                        handleBack={handleBack}
                                        skipIsVisible={false}
                                        forwardIsVisible={true}
                                        forwardIsDisabled={handleDeclarationButton(isProcessing)}
                                        forwardType="submit"
                                        forwardText={"Complete"}
                                    />
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            )}
        </AFLayout>
    );
};

export default AFDeclarationsPage;
